import React from 'react';
import useAsync, { AsyncState } from 'react-use/lib/useAsync';
import { JsonObject } from '@backstage/types';
import { useApi } from '@backstage/core-plugin-api';
import {
  EpDevPBooleanString,
  TEpDevPSortDirection
} from '@solace-labs/backstage-plugin-ep-devp-common';
import {
  IEpDevPSearchListQueryParams,
  IEpDevPSearchListResponse
} from '@solace-labs/backstage-plugin-ep-devp-search-common';
import { epDevPSearchApiClientApiRef } from '../apis';

export type EpDevPSearchContextValue = {
  result: AsyncState<IEpDevPSearchListResponse>;
  setSearchTerm: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSortDirection: React.Dispatch<React.SetStateAction<TEpDevPSortDirection | undefined>>;
  setSortFieldName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setOrganizationId: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDomainIds: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  setExcludeAssetIds: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  setDocumentTypes: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  setCustomFilters: React.Dispatch<React.SetStateAction<JsonObject | undefined>>;
  fetchNextPage?: React.DispatchWithoutAction;
  fetchPreviousPage?: React.DispatchWithoutAction;
} & EpDevPSearchContextState;

export type EpDevPSearchContextState = IEpDevPSearchListQueryParams;

const EpDevPSearchContext = React.createContext<EpDevPSearchContextValue | undefined>(undefined);

export const useEpDevPSearch = (): EpDevPSearchContextValue => {
  const epDevPSearchContextValue = React.useContext(EpDevPSearchContext);
  if (!epDevPSearchContextValue) {
    throw new Error('useEpDevPSearch must be used within a EpDevPSearchContextProvider');
  }
  return epDevPSearchContextValue;
};

export const EpDevPSearchInitialPageSize = 10;

const searchInitialState: EpDevPSearchContextState = {
  searchTerm: '',
  sortDirection: undefined,
  sortFieldName: undefined,
  pageNumber: 1,
  pageSize: EpDevPSearchInitialPageSize,
  isSearchable: EpDevPBooleanString.TRUE,
  filters: {
    organizationId: undefined,
    domainIds: [],
    excludeAssetIds: [],
    documentTypes: [],
    customFilters: {}
  }
};

const useEpDevPSearchContextValue = (
  initialState: EpDevPSearchContextState = searchInitialState
) => {

  const epDevPSearchApiClient = useApi(epDevPSearchApiClientApiRef);

  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(initialState.searchTerm);
  const [sortDirection, setSortDirection] = React.useState<TEpDevPSortDirection | undefined>(initialState.sortDirection);
  const [sortFieldName, setSortFieldName] = React.useState<string | undefined>(initialState.sortFieldName);
  const [pageNumber, setPageNumber] = React.useState<number>(initialState.pageNumber);
  const [pageSize, setPageSize] = React.useState<number>(initialState.pageSize);
  const [organizationId, setOrganizationId] = React.useState<string | undefined>(initialState.filters.organizationId);
  const [domainIds, setDomainIds] = React.useState<string[] | undefined>(initialState.filters.domainIds);
  const [excludeAssetIds, setExcludeAssetIds] = React.useState<string[] | undefined>(initialState.filters.excludeAssetIds);
  const [documentTypes, setDocumentTypes] = React.useState<string[] | undefined>(initialState.filters.documentTypes);
  const [customFilters, setCustomFilters] = React.useState<JsonObject | undefined>(initialState.filters.customFilters);
  /** error handling needs to happen in the outlet */
  const result: AsyncState<IEpDevPSearchListResponse> = useAsync(
    () => {
      const params = {
        searchTerm,
        sortDirection,
        sortFieldName,
        pageNumber,
        pageSize,
        isSearchable: EpDevPBooleanString.TRUE,
        filters: {
          organizationId,
          domainIds,
          excludeAssetIds,
          documentTypes,
          customFilters,
          isSearch: EpDevPBooleanString.TRUE,
        },
      };
      return epDevPSearchApiClient.list(params);
    },
    [searchTerm, sortDirection, sortFieldName, pageNumber, pageSize, organizationId, domainIds, excludeAssetIds, documentTypes, customFilters],
  );

  const hasNextPage = !result.loading && !result.error && result.value?.meta.pagination.nextPage;
  const hasPreviousPage = !result.loading && !result.error && (result.value?.meta.pagination.nextPage && result.value?.meta.pagination.nextPage > 1);

  const fetchNextPage = React.useCallback(() => {
    if (result.value?.meta.pagination.nextPage === undefined) return;
    setPageNumber(result.value.meta.pagination.nextPage)
  }, [result.value?.meta.pagination.nextPage]);

  const fetchPreviousPage = React.useCallback(() => {
    if (result.value?.meta.pagination.nextPage === undefined) return;
    if (result.value.meta.pagination.nextPage > 1) setPageNumber(result.value.meta.pagination.nextPage - 1);
  }, [result.value?.meta.pagination.nextPage]);

  const epDevPSearchContextValue: EpDevPSearchContextValue = {
    result,
    searchTerm, setSearchTerm,
    sortDirection, setSortDirection,
    sortFieldName, setSortFieldName,
    pageNumber, setPageNumber,
    pageSize, setPageSize,
    isSearchable: EpDevPBooleanString.TRUE,
    setOrganizationId,
    setDomainIds,
    setExcludeAssetIds,
    setDocumentTypes,
    setCustomFilters,
    filters: {
      organizationId,
      domainIds,
      excludeAssetIds,
      documentTypes,
      customFilters,
    },
    fetchNextPage: hasNextPage ? fetchNextPage : undefined,
    fetchPreviousPage: hasPreviousPage ? fetchPreviousPage : undefined,
  };
  return epDevPSearchContextValue;
};

type LocalSearchContextProps = React.PropsWithChildren<{
  initialState?: EpDevPSearchContextState;
}>;

const LocalSearchContext = (props: EpDevPSearchContextProviderProps) => {
  const { initialState, children } = props;
  const value = useEpDevPSearchContextValue(initialState);
  // console.log(`LocalSearchContext - value.result.error=${JSON.stringify(value.result.error, null, 2)}`);
  // console.log(`LocalSearchContext - value.result.loading=${JSON.stringify(value.result.loading, null, 2)}`);
  // console.log(`LocalSearchContext - value.result.value=${JSON.stringify(value.result.value, null, 2)}`);
  return (
    <EpDevPSearchContext.Provider value={value}>
      {children}
    </EpDevPSearchContext.Provider>
  );
};

export type EpDevPSearchContextProviderProps = LocalSearchContextProps;

export const EpDevPSearchContextProvider = (props: EpDevPSearchContextProviderProps) => {
  return (
    <LocalSearchContext initialState={props.initialState}>
      {props.children}
    </LocalSearchContext>
  );
};
